import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  type MenuPlacement,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import { clsx } from 'clsx';
import Tooltip from 'components/Tooltip/Tooltip';

import type { ISelect } from './interfaces/ISelect';

import styles from './sass/Select.module.scss';

const OPTION_HEIGHT = 44;
// react-select's own default maxMenuHeight
const DEFAULT_MENU_HEIGHT = 300;

function StyledSelect({
  options,
  onChange,
  value,
  placeholder,
  required,
  label,
  error,
  name,
  isDisabled,
  className,
  placeholderClassName,
  menuPortalClassName,
  menuPortalTarget,
  maxMenuHeight,
  modalRef,
  isSearchable = false,
  menuPosition = 'fixed',
  isMulti = false,
  isAsync = false,
  isClearable = false,
  loadOptions,
  defaultOptions,
  hideErrorText = false,
  classNameMenu,
  icon,
  hint,
  bottomDirection,
  formatOptionLabel,
  inputValue,
  onInputChange,
  isLoading,
  closeMenuOnSelect,
  hideSelectedOptions,
  components: customComponents,
  noOptionsMessage,
  loadingMessage,
}: ISelect) {
  const { t } = useTranslation();
  const selectRef = useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = useState<MenuPlacement>('auto');
  const [open, setOpen] = useState(false);

  const SelectComponent = isAsync ? AsyncSelect : Select;

  useEffect(() => {
    if (selectRef.current && open && modalRef?.current) {
      const rect = selectRef.current.getBoundingClientRect();
      const modalRect = modalRef.current.getBoundingClientRect();
      // An async select fills its menu from `loadOptions` after this runs, so there is no list to
      // measure — fall back to the default menu height instead of measuring zero and always
      // deciding there is room below.
      const dropdownHeight = isAsync ? DEFAULT_MENU_HEIGHT : (options?.length || 0) * OPTION_HEIGHT;

      // Determine the position taking into account the scrolling
      const spaceBelow = modalRect.bottom - rect.bottom;
      const spaceAbove = rect.top - modalRect.top;

      const enoughSpaceBelow = spaceBelow >= (maxMenuHeight || dropdownHeight);
      const enoughSpaceAbove = spaceAbove >= (maxMenuHeight || dropdownHeight);

      if (enoughSpaceBelow || bottomDirection) {
        setMenuPlacement('bottom');
      } else if (enoughSpaceAbove) {
        setMenuPlacement('top');
      } else {
        // If there is not enough space either at the top or bottom, choose the side with the most space.
        setMenuPlacement(spaceBelow > spaceAbove || bottomDirection ? 'bottom' : 'top');
      }
    }
  }, [selectRef, maxMenuHeight, open, options, modalRef, bottomDirection, isAsync]);

  return (
    <div className={clsx(styles.root, className)} ref={selectRef}>
      {label ? (
        <p className={styles.label}>
          {required ? `${label}*` : label}
          {icon
            ? (
              <Tooltip hint={hint} hintClassName={styles.hint}>
                <span className={styles.icon}>{icon}</span>
              </Tooltip>
            )
            : null}
        </p>
      ) : null}
      <SelectComponent
        classNames={{
          control: () => clsx(styles.control, {
            [styles.controlError]: error,
          }),
          placeholder: () => clsx(styles.placeholder, placeholderClassName),
          indicatorSeparator: () => styles.separator,
          indicatorsContainer: () => styles.chevron,
          menuPortal: () => clsx(styles.menuPortal, menuPortalClassName),
          menu: () => clsx(styles.menu, classNameMenu),
          option: (state) => clsx(styles.option, {
            [styles.optionSelected]: state.isSelected,
          }),
          multiValue: () => styles.customMultiValue,
          singleValue: () => styles.customSingleValue,
        }}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        name={name}
        value={value}
        options={options}
        placeholder={placeholder}
        isSearchable={isSearchable}
        required={required}
        onChange={onChange}
        menuPosition={menuPosition}
        menuPortalTarget={menuPortalTarget}
        isDisabled={isDisabled}
        maxMenuHeight={maxMenuHeight}
        menuPlacement={menuPlacement}
        onMenuOpen={() => setOpen(true)}
        onMenuClose={() => setOpen(false)}
        loadOptions={isAsync ? loadOptions : undefined}
        defaultOptions={isAsync ? defaultOptions : undefined}
        isMulti={isMulti}
        closeMenuOnSelect={closeMenuOnSelect ?? !isMulti}
        hideSelectedOptions={hideSelectedOptions}
        components={{
          ...(isAsync ? { DropdownIndicator: null } : {}),
          ...customComponents,
        }}
        noOptionsMessage={noOptionsMessage ?? (({ inputValue: search }) => (
          // Default: an async select with no default list can only fill from a search, so it
          // prompts for one. Selects that carry their own options fall through to the empty
          // message. Either message can be overridden per select.
          isAsync && !defaultOptions?.length && !search ? t('common.startTyping') : t('common.noResultsFound')
        ))}
        loadingMessage={loadingMessage ?? (() => t('common.searching'))}
        formatOptionLabel={formatOptionLabel}
        isClearable={isClearable}
        inputValue={inputValue}
        onInputChange={onInputChange}
        isLoading={isLoading}
        menuShouldScrollIntoView={false}
      />
      {error && !hideErrorText ? <p className={styles.error}>{error}</p> : null}
    </div>
  );
}

export default StyledSelect;
